import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";

export default function Teatro() {
  const { i18n } = useTranslation();
  const isIT = i18n.language.startsWith("it");

  const bullets = isIT
    ? [
        { t: "Simulazione in scala reale 1:1", d: "Un ambiente fisico con moduli componibili a grandezza reale." },
        { t: "Coinvolgimento interattivo", d: "Il cliente diventa co-protagonista attivo del proprio spazio, non semplice spettatore." },
        { t: "Test di configurazione e arredi", d: "Valutazione diretta di distanze, ingombri e accostamenti." },
        { t: "Metodo empirico mirato", d: "Visualizzazione immediata che azzera le incertezze e previene ogni criticità progettuale." },
      ]
    : [
        { t: "1:1 real-scale simulation", d: "A physical environment with modular real-size blocks." },
        { t: "Interactive involvement", d: "The client becomes an active co-protagonist of their own space, not just a spectator." },
        { t: "Layout & furniture testing", d: "Direct assessment of distances, volumes and combinations." },
        { t: "Targeted empirical method", d: "Immediate visualisation that eliminates uncertainty and prevents any design issue." },
      ];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <div className="text-center">
        <h1 className="font-serif text-5xl lg:text-6xl text-stone-900" data-testid="teatro-title">
          {isIT ? "Cosa Ci Rende Unici" : "What Makes Us Unique"}
        </h1>
        <div className="mt-4 w-16 h-[2px] bg-[#D06D4D] mx-auto" aria-hidden="true" />
      </div>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Text left */}
        <div>
          <div className="eyebrow">{isIT ? "Teatro Dinamico" : "Dynamic Theatre"}</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-4 text-stone-900" data-testid="teatro-heading">
            {isIT
              ? "Visualizza il tuo progetto in scala reale."
              : "See your project at real scale."}
          </h2>

          <div className="mt-6 space-y-5 text-stone-700 text-lg leading-relaxed">
            <p>
              {isIT
                ? "Oltre i limiti della rappresentazione virtuale, il nostro studio propone un approccio totalmente tangibile ed empirico. Mettiamo a disposizione un vero e proprio \u201Cteatro\u201D fisico dove poter riprodurre la tua stanza in scala 1:1, permettendoti di vivere, toccare e testare il progetto prima ancora che parta il cantiere."
                : "Beyond the limits of virtual representation, our studio offers a fully tangible and empirical approach. We provide a real physical \u201Ctheatre\u201D where your room can be reproduced at 1:1 scale, letting you live, touch and test the project before construction even begins."}
            </p>
            <p>
              {isIT
                ? "Attraverso l'uso di moduli e blocchi componibili, potrai spostare direttamente gli arredi per valutare distanze, proporzioni, percorsi e ingombri reali, azzerando ogni incomprensione e prevenendo le criticità."
                : "Using modular movable blocks, you can directly rearrange furniture to assess distances, proportions, paths and real volumes — eliminating any misunderstanding and preventing critical issues."}
            </p>
          </div>

          <ul className="mt-8 space-y-4">
            {bullets.map((b, i) => (
              <li key={typeof b === "string" ? b : i} className="flex items-start gap-3 text-stone-800" data-testid={`teatro-bullet-${i}`}>
                <CheckCircle2 size={22} className="text-[#D06D4D] flex-shrink-0 mt-0.5" strokeWidth={1.7} />
                <span className="text-base leading-relaxed">
                  <span className="font-semibold text-stone-900">{b.t}:</span> <span className="text-stone-700">{b.d}</span>
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex justify-center">
            <a
              href="https://calendly.com/planit-studio/teatro-dinamico"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="teatro-cta"
              className="inline-flex items-center gap-2 bg-[#D06D4D] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#b95a3d] transition-colors"
            >
              {isIT ? "Prenota una visita" : "Book a visit"} <ArrowUpRight size={16} />
            </a>
          </div>
        </div>

        {/* Image right */}
        <div className="relative">
          <div className="aspect-[4/5] overflow-hidden rounded-2xl shadow-[8px_8px_24px_rgba(28,25,23,0.12)]">
            <img
              src="https://i.ibb.co/JjPxkY2k/Gemini-Generated-Image-6luxyo6luxyo6lux.jpg"
              alt={isIT ? "Teatro Dinamico Plan It Studio" : "Plan It Studio Dynamic Theatre"}
              className="w-full h-full object-cover"
              data-testid="teatro-image"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
